import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Stack, TextField } from '@mui/material'
import React from 'react'
import { NumberField } from 'src/Libs/inputs/formik'
import Select from 'src/Libs/inputs/formik/Select'
import { UnitTypes } from 'src/Libs/types'
import useLabeledPhotovoltaicOptions from '../../../hooks/use-labled-photovoltaic-options'
import { PhotovoltaicStringModel } from '../../../models/photovoltaic'
import { OptionalTooltipWrapper } from '@e3dc-react/shell/Elements/Tooltip'
import SelectCreatableFetched from 'src/Libs/inputs/formik/SelectCreatableFetched'
import { useField } from 'formik'
import { calculateKwp, mapPVModuleToLabeledOption } from 'src/Libs/installation-statement/utils/photovoltaic'
import { PvModuleModel } from 'src/Routes/Administration/PvModules/PvModuleModels'

interface Props {
  trackerIndex: number
  stringIndex: number
  disabled: boolean
}

/**
 * This component displays all fields relevant for a string
 */
const StringFields: React.VFC<Props> = ({ trackerIndex, stringIndex, disabled }) => {
  const { t } = useCustomTranslation()
  const { powerOptimizerOptions, directionOptions } = useLabeledPhotovoltaicOptions()
  const getPath = (param: keyof PhotovoltaicStringModel): string => `pvTrackers.${trackerIndex}.pvStrings.${stringIndex}.${param}`

  const [moduleCount] = useField(getPath('moduleCount'))
  const [modulePeakPower] = useField(getPath('modulePeakPower'))

  return (
    <Stack spacing={4} marginTop={2}>
      <SelectCreatableFetched<PvModuleModel>
        name={getPath('pvModuleType')}
        filterByParameter={'MODUL'}
        label={t('installation-statement.photovoltaic.form.string.module-type')}
        url="pv-modules"
        mapFunction={mapPVModuleToLabeledOption}
        clearable
        required
        disabled={disabled}
        minCharactersToStartSearch={3}
      />
      <NumberField
        disabled={disabled}
        required
        label={t('installation-statement.photovoltaic.form.string.module-count')}
        name={getPath('moduleCount')}
        ignoreDefaultValue
      />
      <Select
        disabled={disabled}
        required
        label={t('installation-statement.photovoltaic.form.string.power-optimizer')}
        name={getPath('powerOptimizer')}
        options={powerOptimizerOptions}
      />
      <Select
        disabled={disabled}
        required
        label={t('installation-statement.photovoltaic.form.string.direction')}
        name={getPath('direction')}
        options={directionOptions}
      />
      <NumberField
        disabled={disabled}
        required
        label={t('installation-statement.photovoltaic.form.string.orientation')}
        name={getPath('orientation')}
        suffix={UnitTypes.degree}
        ignoreDefaultValue
      />
      <NumberField
        disabled={disabled}
        required
        label={t('installation-statement.photovoltaic.form.string.test-voltage')}
        name={getPath('testVoltage')}
        suffix={UnitTypes.volt}
        ignoreDefaultValue
      />
      <NumberField
        disabled={disabled}
        required
        label={t('installation-statement.photovoltaic.form.string.module-peak-power')}
        name={getPath('modulePeakPower')}
        suffix={UnitTypes.wattPeak}
        isDecimal
        precision={2}
        ignoreDefaultValue
      />
      <NumberField
        disabled={disabled}
        required
        label={t('installation-statement.photovoltaic.form.string.open-circuit-voltage')}
        name={getPath('openCircuitVoltage')}
        suffix={UnitTypes.volt}
        ignoreDefaultValue
      />
      <NumberField
        disabled={disabled}
        required
        label={t('installation-statement.photovoltaic.form.string.positive-electrode-ground')}
        suffix={UnitTypes.megaOhm}
        name={getPath('positiveElectrodeGround')}
        ignoreDefaultValue
      />
      <NumberField
        disabled={disabled}
        required
        label={t('installation-statement.photovoltaic.form.string.negative-electrode-ground')}
        suffix={UnitTypes.megaOhm}
        name={getPath('negativeElectrodeGround')}
        ignoreDefaultValue
      />
      <NumberField
        disabled={disabled}
        required
        label={t('installation-statement.photovoltaic.form.string.riso')}
        suffix={UnitTypes.megaOhm}
        name={getPath('riso')}
        ignoreDefaultValue
      />
      <OptionalTooltipWrapper titleKey={'installation-statement.photovoltaic.form.string.kwp-tooltip'}>
        <TextField
          disabled={true}
          required
          label={t('installation-statement.photovoltaic.form.string.kwp')}
          type="number"
          value={calculateKwp(moduleCount.value, modulePeakPower.value)}
          InputLabelProps={{ shrink: Boolean(calculateKwp(moduleCount.value, modulePeakPower.value)) }}
        />
      </OptionalTooltipWrapper>
    </Stack>
  )
}

export default StringFields
